import { useState, useEffect, useCallback } from 'react';
import { ALL_COLUMNS, DEFAULT_VISIBLE_COLUMNS } from '@/components/customers/column-config-dialog';

const STORAGE_KEY = 'customers-table-column-preferences';

export function useCustomerColumnPreferences() {
  const [visibleColumns, setVisibleColumns] = useState<string[]>(DEFAULT_VISIBLE_COLUMNS);
  const [isLoading, setIsLoading] = useState(true);

  // Load preferences from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as string[];
        // Validate that stored columns are valid
        const validColumns = parsed.filter(colId => 
          ALL_COLUMNS.some(col => col.id === colId)
        );
        if (validColumns.length > 0) {
          setVisibleColumns(validColumns);
        } else {
          setVisibleColumns(DEFAULT_VISIBLE_COLUMNS);
        }
      }
    } catch (error) {
      console.error('Failed to load customer column preferences:', error);
      // Fall back to defaults on error
      setVisibleColumns(DEFAULT_VISIBLE_COLUMNS);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Save preferences to localStorage
  const savePreferences = useCallback((newColumns: string[]) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newColumns));
      setVisibleColumns(newColumns);
    } catch (error) {
      console.error('Failed to save customer column preferences:', error);
    }
  }, []);

  // Reset to default configuration
  const resetToDefaults = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
      setVisibleColumns(DEFAULT_VISIBLE_COLUMNS);
    } catch (error) {
      console.error('Failed to reset customer column preferences:', error);
    }
  }, []);

  return {
    visibleColumns,
    setVisibleColumns: savePreferences,
    isLoading,
    resetToDefaults,
  };
}
